/**
 * 画布「生成配置节点」参数记忆（localStorage）
 *
 * 用户在某类配置节点里选过的模型 / 时长 / 清晰度 / 比例等参数会被记住，
 * 下次新建同类节点时自动套用，免去每次重新选择。
 *
 * 设计约束：
 *  - 只记忆「用户偏好型」参数（白名单字段），不记业务内容（prompt、计划项、状态等）；
 *  - 只记「用户主动选择」产生的写入（组件 onChange），系统派生校正（updateNodeDataSilent）不入库；
 *  - 记忆的模型可能已下线 / 被隐藏，由节点组件在模型列表就绪后校验并回退首个可用模型；
 *  - localStorage 不可用（隐私模式 / 配额）时静默失效，不影响主流程。
 */
import type { AgentNodeData, GenerateImageNodeData, GenerateVideoNodeData } from '../types';

export type NodeParamMemoryKind = 'generate_image' | 'generate_video' | 'agent';

interface MemoryShape {
  generate_image: Partial<GenerateImageNodeData>;
  generate_video: Partial<GenerateVideoNodeData>;
  agent: Partial<AgentNodeData>;
}

const STORAGE_KEY = 'canvas_node_param_memory_v1';

/**
 * 各类节点允许记忆的字段白名单。
 * 刻意不含 `mode`：模式由上游连线驱动（有无参考图自动切换），记忆容易与当前上游状态冲突。
 */
const ALLOWED_FIELDS: Record<NodeParamMemoryKind, readonly string[]> = {
  generate_image: ['modelId', 'aspectRatio', 'quality', 'count'],
  generate_video: [
    'modelId',
    'duration',
    'resolution',
    'aspectRatio',
    'cameraMovement',
    'motionIntensity',
    'enableAudio',
    'count',
  ],
  agent: ['modelId', 'aspectRatio', 'quality', 'useChainReference'],
};

type StoredAll = Partial<Record<NodeParamMemoryKind, Record<string, unknown>>>;

function readAll(): StoredAll {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as StoredAll) : {};
  } catch {
    return {};
  }
}

function writeAll(all: StoredAll): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch {
    /* 记忆功能静默失效 */
  }
}

/** 读取某类节点的记忆参数（仅返回白名单字段） */
export function readNodeParamMemory<K extends NodeParamMemoryKind>(
  kind: K
): MemoryShape[K] {
  const stored = readAll()[kind];
  if (!stored || typeof stored !== 'object') return {} as MemoryShape[K];
  const allowed = ALLOWED_FIELDS[kind];
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(stored)) {
    if (allowed.includes(key) && value !== undefined) result[key] = value;
  }
  return result as MemoryShape[K];
}

/** 合并写入某类节点的记忆参数（仅保存白名单字段） */
export function writeNodeParamMemory<K extends NodeParamMemoryKind>(
  kind: K,
  patch: Partial<MemoryShape[K]>
): void {
  const allowed = ALLOWED_FIELDS[kind];
  const filtered: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(patch)) {
    if (allowed.includes(key) && value !== undefined) filtered[key] = value;
  }
  if (Object.keys(filtered).length === 0) return;

  const all = readAll();
  all[kind] = { ...(all[kind] ?? {}), ...filtered };
  writeAll(all);
}
